import React from "react";
import { useTranslation } from "react-i18next";
import { useTelemetry } from "../../context/TelemetryContext";
import { useIndustry } from "../../context/IndustryContext";
import ModeIndicator from "../../components/shared/ModeIndicator";
import LanguageToggle from "../../components/shared/LanguageToggle";
import SubPageNav from "../../components/shared/SubPageNav";

const INDUSTRY_ZONE_KEY = {
  "School": "school",
  "Airport": "airport",
  "Public Event": "publicEvent",
  "Border Security": "borderSecurity",
  "Military": "military",
};

function ThreatZones() {
const { t } = useTranslation();

const telemetry = useTelemetry();
const { industry } = useIndustry();

const zoneNames = t(`threatZones.zoneNames.${INDUSTRY_ZONE_KEY[industry] || "default"}`, { returnObjects: true });

const zoneRisks = [
  { zone: zoneNames[0], risk: "CRITICAL" },
  { zone: zoneNames[1], risk: "WARNING" },
  { zone: zoneNames[2], risk: "SAFE" }
];

const highestRiskZone =
zoneRisks.find(
  z => z.risk === "CRITICAL"
)?.zone || t("threatZones.noActiveThreat");

return (

<div
style={{
padding:"20px",
color:"white"
}}
>

<div style={{position:"relative",textAlign:"center",marginBottom:"15px"}}>
<h1
style={{
margin:0,textAlign:"center"
}}
>
{t("threatZones.title")}
</h1>
<div style={{position:"absolute",top:0,right:0,display:"flex",alignItems:"flex-start",gap:"12px"}}><LanguageToggle /><ModeIndicator /></div>
</div>

<p
style={{
color:"#94a3b8",
marginBottom:"25px",textAlign:"center"
}}
>
{t("threatZones.subtitle")}
</p>

{/* ===================================================== */}
{/* REGIONAL RISK MAP + GEO OVERLAY */}
{/* ===================================================== */}

<div
style={{
display:"grid",
gridTemplateColumns:"1fr 1fr",
gap:"20px"
}}
>

<div
style={{
background:"#111827",
padding:"20px",
borderRadius:"14px",
border:"1px solid #1e293b"
}}
>

<h3>{t("threatZones.regionalRiskMap.title")}</h3>

<p style={{color:"#94a3b8"}}>
{t("threatZones.regionalRiskMap.subtitle")}
</p>

<div
style={{
marginTop:"20px"
}}
>

{zoneRisks.map((zone, index) => {

const color =
zone.risk === "CRITICAL"
? "#ef4444"
: zone.risk === "WARNING"
? "#f59e0b"
: "#22c55e";

return (

<div
key={index}
style={{
display:"flex",
justifyContent:"space-between",
padding:"12px",
marginBottom:"10px",
background:"#0f172a",
borderRadius:"10px"
}}
>

<span>
{zone.zone}
</span>

<span
style={{
color,
fontWeight:"bold"
}}
>
{t(`commandCenter.risk.${zone.risk}`, zone.risk)}
</span>

</div>

);

})}

</div>

</div>

<div
style={{
background:"#111827",
padding:"20px",
borderRadius:"14px",
border:"1px solid #1e293b"
}}
>

<h3>{t("threatZones.geoSpatialOverlay.title")}</h3>

<p style={{color:"#94a3b8"}}>
{t("threatZones.geoSpatialOverlay.subtitle")}
</p>

<div
style={{
marginTop:"20px"
}}
>

{zoneRisks.map((zone, index) => (

<div
key={index}
style={{
display:"flex",
justifyContent:"space-between",
marginBottom:"12px"
}}
>

<span>{zone.zone}</span>

<span
style={{
fontWeight:"bold",
color:
zone.risk === "CRITICAL"
? "#ef4444"
: zone.risk === "WARNING"
? "#f59e0b"
: "#22c55e"
}}
>
{t(`commandCenter.risk.${zone.risk}`, zone.risk)}
</span>

</div>

))}

</div>

</div>

</div>

{/* ===================================================== */}
{/* THREAT HEAT MAP */}
{/* ===================================================== */}

<div
style={{
marginTop:"20px",
background:"#111827",
padding:"20px",
borderRadius:"14px",
border:"1px solid #1e293b"
}}
>

<h3
style={{
textAlign:"center",
marginBottom:"25px"
}}
>
{t("threatZones.threatHeatMap.title")}
</h3>

<div
style={{
display:"grid",
gridTemplateColumns:"repeat(3,1fr)",
gap:"20px"
}}
>

{[
{zone:zoneNames[0],score:92},
{zone:zoneNames[1],score:75},
{zone:zoneNames[2],score:35}
].map((item,index)=>(

<div key={index}>

<div
style={{
marginBottom:"10px"
}}
>
{item.zone}
</div>

<div
style={{
height:"16px",
background:"#0f172a",
borderRadius:"10px",
overflow:"hidden"
}}
>

<div
style={{
width:`${item.score}%`,
height:"100%",
background:
item.score > 80
? "#ef4444"
: item.score > 60
? "#f59e0b"
: "#22c55e"
}}
/>

</div>

<div
style={{
marginTop:"8px"
}}
>
{item.score}
</div>

</div>

))}

</div>

</div>

{/* ===================================================== */}
{/* LIVE THREAT INTELLIGENCE */}
{/* ===================================================== */}

<div
style={{
marginTop:"20px",
background:"#111827",
padding:"20px",
borderRadius:"14px",
border:"1px solid #1e293b"
}}
>

<h3
style={{
textAlign:"center",
marginBottom:"20px"
}}
>
{t("threatZones.liveThreatIntelligence.title")}
</h3>

<div
style={{
display:"grid",
gridTemplateColumns:"repeat(4,1fr)",
gap:"15px",
textAlign:"center"
}}
>

<div>
<h4>{t("threatZones.liveThreatIntelligence.highestRiskZone")}</h4>
<p>{highestRiskZone}</p>
</div>

<div>
<h4>{t("threatZones.liveThreatIntelligence.activeThreats")}</h4>
<p>{telemetry.fentanyl_ppb}</p>
</div>

<div>
<h4>{t("threatZones.liveThreatIntelligence.lastAlert")}</h4>
<p>{t("threatZones.liveThreatIntelligence.lastAlertValue")}</p>
</div>

<div>
<h4>{t("threatZones.liveThreatIntelligence.aiConfidence")}</h4>
<p>{telemetry.aiConfidence}%</p>
</div>

</div>

</div>

{/* ===================================================== */}
{/* ZONE TELEMETRY MATRIX */}
{/* ===================================================== */}

<div
style={{
marginTop:"20px",
background:"#111827",
padding:"20px",
borderRadius:"14px",
border:"1px solid #1e293b"
}}
>

<h3
style={{
textAlign:"center",
marginBottom:"20px"
}}
>
{t("threatZones.zoneTelemetryMatrix.title")}
</h3>

<table
style={{
width:"100%",
textAlign:"center"
}}
>

<thead>
<tr>
<th>{t("threatZones.zoneTelemetryMatrix.zone")}</th>
<th>{t("threatZones.zoneTelemetryMatrix.pm25")}</th>
<th>{t("threatZones.zoneTelemetryMatrix.voc")}</th>
<th>{t("threatZones.zoneTelemetryMatrix.exposure")}</th>
</tr>
</thead>

<tbody>

{zoneNames.map((zone,index)=>(

<tr key={index}>
<td>{zone}</td>
<td>{Math.max(5, telemetry.pm25 - index * 10)}</td>
<td>{Math.max(20, telemetry.voc - index * 20)}</td>
<td>{Math.max(0, telemetry.fentanyl_ppb - index)}</td>
</tr>

))}

</tbody>

</table>

</div>

{/* ===================================================== */}
{/* SECURITY RECOMMENDATIONS */}
{/* ===================================================== */}

<div
style={{
marginTop:"20px",
background:"#111827",
padding:"20px",
borderRadius:"14px",
border:"1px solid #1e293b"
}}
>

<h3
style={{
textAlign:"center",
marginBottom:"20px"
}}
>
{t("threatZones.securityRecommendations.title")}
</h3>

<div
style={{
lineHeight:"2",
textAlign:"center"
}}
>

<p>{t("threatZones.securityRecommendations.increaseMonitoring")}</p>
<p>{t("threatZones.securityRecommendations.dispatchSecurityTeam")}</p>
<p>{t("threatZones.securityRecommendations.restrictZoneAccess")}</p>
<p>{t("threatZones.securityRecommendations.maintainHumanOversight")}</p>

</div>

</div>

{/* ===================================================== */}
{/* THREAT AGENT STATUS */}
{/* ===================================================== */}

<div
style={{
marginTop:"20px",
background:"#111827",
padding:"20px",
borderRadius:"14px",
border:"1px solid #1e293b"
}}
>

<h3
style={{
textAlign:"center",
marginBottom:"20px"
}}
>
{t("threatZones.threatAgentNetwork.title")}
</h3>

<div
style={{
display:"grid",
gridTemplateColumns:"repeat(4,1fr)",
gap:"15px",
textAlign:"center"
}}
>

<div>
<h4>{t("threatZones.threatAgentNetwork.threatAgent")}</h4>
<p style={{color:"#22c55e"}}>{t("commandCenter.status.active")}</p>
</div>

<div>
<h4>{t("threatZones.threatAgentNetwork.forecastAgent")}</h4>
<p style={{color:"#22c55e"}}>{t("commandCenter.status.active")}</p>
</div>

<div>
<h4>{t("threatZones.threatAgentNetwork.recommendationAgent")}</h4>
<p style={{color:"#22c55e"}}>{t("commandCenter.status.active")}</p>
</div>

<div>
<h4>{t("threatZones.threatAgentNetwork.governanceAgent")}</h4>
<p style={{color:"#22c55e"}}>{t("commandCenter.status.active")}</p>
</div>

</div>

</div>

<SubPageNav current="threatZones" />

</div>

);

}

export default ThreatZones;
